import React, { useEffect, useState } from 'react';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { Range } from 'react-range';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import useLocale from 'helpers/hooks/useLocale';
import { useAccount } from 'frontastic';

const FacetFilters = ({
  facet,
  onFiltersChange,
  maxPrice,
  lowPrice,
  priceRange,
  setPriceRange,
  isPriceRangeReady,
}: any) => {
  const [selectedFilters, setSelectedFilters] = useState<any>({});
  const [selectedColours, setSelectedColours] = useState<string[]>([]);
  const [visibleCounts, setVisibleCounts] = useState<{ [key: string]: number }>({});
  const [dragging, setDragging] = useState(false);
  const { locale } = useLocale();
  const { loggedIn, result } = useAccount();

  const formatFacetKeyForDisplay = (key: string) => {
    if (key === 'categoryKeys.en-GB') return 'CATEGORIES';
    if (key === 'variants.attributes.colour') return 'COLOUR';
    if (key === 'variants.attributes.size') return 'SIZE';
    if (key === 'variants.attributes.waist') return 'WAIST';
    if (key === 'variants.attributes.shoesize') return 'SHOE SIZE';
    if (key === 'variants.attributes.hand') return 'HAND';

    return key.replace('attributes.', '').replace(/_/g, ' ').replace(/-/g, ' ').toUpperCase();
  };

  const userId = loggedIn ? result?.data?.data?.account?.accountId : localStorage.getItem('__anon_id');

  const handleFacetClick = (locale: string, filter: string[]) => {
    const analytics = new AlgoliaAnalytics(locale);
    analytics.handleFilterClick(userId, filter);
  };
  const handleFacetChange = (facetType: string, value: string) => {
    handleFacetClick(locale, [`${facetType}: ${value}`]);

    const updatedFilters = { ...selectedFilters };
    const currentSelections = updatedFilters[facetType] || [];

    if (currentSelections.includes(value)) {
      updatedFilters[facetType] = currentSelections.filter((item: string) => item !== value);
    } else {
      updatedFilters[facetType] = [...currentSelections, value];
    }

    setSelectedFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleColourClick = (colour: string) => {
    handleFacetClick(locale, [`${'variants.attributes.colour'}: ${colour}`]);
    const newSelectedColours = selectedColours.includes(colour)
      ? selectedColours.filter((selectedColour) => selectedColour !== colour)
      : [...selectedColours, colour];

    setSelectedColours(newSelectedColours);
    onFiltersChange({ ...selectedFilters, 'variants.attributes.colour': newSelectedColours });
  };

  const handleReadMore = (facetType: string) => {
    setVisibleCounts((prev) => {
      const currentVisible = prev[facetType] || 10;
      const totalItems = Object.keys(facet[facetType] || {}).length;
      return {
        ...prev,
        [facetType]: currentVisible >= totalItems ? 10 : totalItems,
      };
    });
  };

  // Handle smooth UI updates during drag
  const handleOnChange = (values: number[]) => {
    let [newMin, newMax] = values;

    // Prevent values from resetting to 0
    if (newMin < lowPrice) newMin = lowPrice;
    if (newMax > maxPrice) newMax = maxPrice;

    // Ensure a minimum distance of 10
    if (newMax - newMin < 10) {
      if (newMin === lowPrice) {
        newMax = newMin + 10;
      } else {
        newMin = newMax - 10;
      }
    }

    setPriceRange([newMin, newMax]);
  };

  // Ensure final change updates the filters properly
  const handleFinalChange = (values: number[]) => {
    let [newMin, newMax] = values;

    newMin = Math.max(lowPrice, newMin);
    newMax = Math.min(maxPrice, newMax);

    // Ensure a minimum distance of 10
    if (newMax - newMin < 10) {
      if (newMin === lowPrice) {
        newMax = newMin + 10;
      } else {
        newMin = newMax - 10;
      }
    }

    setPriceRange([newMin, newMax]);

    onFiltersChange((prevFilters: any) => ({
      ...prevFilters,
      priceRange: [newMin, newMax],
    }));
  };

  const getGradientStyle = (colourString: string): React.CSSProperties => {
    const coloursArray = colourString.split('/');
    const validColours = coloursArray.filter((color) => color.trim() !== '');
    if (validColours.length === 0) {
      return { background: 'linear-gradient(to right, #003366, #66ccff)' };
    }
    const adjustedColours = validColours.map((color) => {
      if (isColorCloseToWhite(color)) {
        return '#808080';
      }
      return color.trim();
    });
    const gradientStops = adjustedColours.map(
      (color, index) => `${color} ${(index / (adjustedColours.length - 1)) * 100}%`,
    );
    return {
      background: `linear-gradient(to right, ${gradientStops.join(', ')})`,
    };
  };

  const isColorCloseToWhite = (color: string): boolean => {
    const rgb = hexToRgb(color);
    if (!rgb) return false;

    const { r, g, b } = rgb;
    return r > 200 && g > 200 && b > 200;
  };

  const hexToRgb = (hex: string) => {
    if (!/^#[0-9A-F]{6}$/i.test(hex)) return null;
    let r = 0,
      g = 0,
      b = 0;
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }
    return { r, g, b };
  };

  const renderFacetItems = (facetType: string, facetData: any) => {
    const visibleCount = visibleCounts[facetType] || 10;
    const facetEntries = Object.entries(facetData);

    const hasMoreContent = visibleCount < facetEntries.length;

    return (
      <>
        {facetEntries.slice(0, visibleCount).map(([key, count]: any, index) => (
          <li key={index} className="mb-3 flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`${facetType}-${index}`}
                className="custom-checkbox mr-8 cursor-pointer rounded-md"
                checked={selectedFilters[facetType]?.includes(key) || false}
                onChange={() => handleFacetChange(facetType, key)}
              />
              <div className="flex items-start">
                <p className="cursor-pointer text-start font-body text-16 font-medium capitalize leading-5">{key}</p>
              </div>
            </div>
            <p className="text-neutral-500">({count})</p>
          </li>
        ))}
        {facetEntries.length > 10 && (
          <li className="mt-15 flex justify-center">
            <button className="cursor-pointer" onClick={() => handleReadMore(facetType)}>
              {hasMoreContent ? (
                <p className="flex items-center text-16 font-medium leading-5">
                  <span className="mr-3 text-20 text-[#127e57]">+</span>Show All
                </p>
              ) : (
                <p className="flex items-center text-16 font-medium leading-5">
                  <span className="mr-3 text-20 text-[#127e57]">-</span>Collapse
                </p>
              )}
            </button>
          </li>
        )}
      </>
    );
  };

  const renderColourFacet = (facetData: any) => {
    const isValidGradient = (color: string): boolean => {
      const gradientStyle: any = getGradientStyle(color)?.background || '';
      const invalidGradients = [
        'linear-gradient(to right, black 0%, quiet shade 100%)',
        'linear-gradient(to right, black 0%, black 50%, reflective silver 100%)',
      ];
      return !/NaN%|white\s*100%/.test(gradientStyle) && !invalidGradients.includes(gradientStyle);
    };

    const colours = Object.keys(facetData || {}).filter(
      (colour) => colour && colour.trim() !== '' && isValidGradient(colour),
    );

    if (colours.length === 0) {
      return null;
    }

    return (
      <AccordionBtn
        variant="plusAndMinus"
        className="col-span-2 border-b border-neutral-400 text-16 font-bold leading-7 md:mt-0"
        closedSectionTitle="COLOUR"
        buttonClassName="py-15 font-bold text-16 leading-tight text-primary-black pl-0 md:pr-12"
        sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
        panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
        defaultOpen={true}
      >
        <div className="flex flex-wrap gap-5">
          {colours.map((colour: string, index: number) => {
            const color = getGradientStyle(colour);
            return (
              <button
                className={`h-30 w-30 rounded-md border-2 ${
                  selectedColours.includes(colour) ? 'border-[#127e57]' : 'border-neutral-300'
                }`}
                style={getGradientStyle(colour)}
                key={index}
                aria-label={`Select ${colour}`}
                onClick={() => handleColourClick(colour)}
              >
                {selectedColours.includes(colour) && <span className="font-bold text-white">✔</span>}
              </button>
            );
          })}
        </div>
      </AccordionBtn>
    );
  };

  const renderFacets = () => {
    return Object.keys(facet).map((facetType) => {
      // if (facetType === 'variants.attributes.colour') {
      //   return renderColourFacet(facet[facetType]);
      // }
      if (
        facetType === 'standalonePrice.priceGB' ||
        facetType === 'categories.en-GB.lvl0' ||
        facetType === 'categories.en-GB.lvl1' ||
        facetType === 'variants.attributes.colour' ||
        facetType === 'attributes.isCustomFit'
      ) {
        return null;
      }

      return (
        <AccordionBtn
          key={facetType}
          variant="plusAndMinus"
          className="col-span-2 border-b border-neutral-400 text-16 font-bold leading-7 md:mt-0"
          closedSectionTitle={formatFacetKeyForDisplay(facetType)}
          sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
          buttonClassName="py-15 font-bold text-16 leading-tight text-primary-black pl-0 md:pr-12"
          panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
          defaultOpen={true}
        >
          <ul>{renderFacetItems(facetType, facet[facetType] || {})}</ul>
        </AccordionBtn>
      );
    });
  };

  return (
    <div className="accordion-section mb-40 border-t border-neutral-400">
      {renderFacets()}
      {!isPriceRangeReady ? (
        <p>Loading...</p>
      ) : (
        <>
          {lowPrice !== maxPrice && (
            <AccordionBtn
              variant="plusAndMinus"
              className="col-span-2 border-b border-neutral-400 text-16 font-bold leading-7 md:mt-0"
              closedSectionTitle="PRICE RANGE"
              buttonClassName="py-15 font-bold text-16 leading-tight text-primary-black pl-0 md:pr-12"
              sectionTileClassName="font-body text-16 font-bold uppercase leading-[28px]"
              panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
              defaultOpen={true}
            >
              <div className="p-4">
                <Range
                  step={0.5}
                  min={lowPrice}
                  max={maxPrice}
                  values={priceRange}
                  onChange={handleOnChange}
                  onFinalChange={handleFinalChange}
                  renderTrack={({ props, children }) => {
                    const [min, max] = priceRange;
                    const percentageMin = ((min - lowPrice) / (maxPrice - lowPrice)) * 100;
                    const percentageMax = ((max - lowPrice) / (maxPrice - lowPrice)) * 100;
                    return (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: '8px',
                          width: '100%',
                          background: `linear-gradient(to right, #ccc ${percentageMin}%, #127e57 ${percentageMin}%, #127e57 ${percentageMax}%, #ccc ${percentageMax}%)`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'background 0.2s ease',
                        }}
                        className="relative bg-neutral-200"
                      >
                        {children}
                      </div>
                    );
                  }}
                  renderThumb={({ props, isDragged }) => (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        height: '22px', // Larger thumb for easier grip
                        width: '22px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '2px solid #127e57',
                        boxShadow: isDragged ? '0 0 10px rgba(0,0,0,0.3)' : 'none',
                        transition: 'box-shadow 0.2s ease, transform 0.1s ease',
                        transform: isDragged ? 'scale(1.1)' : 'scale(1)', // Adds a slight expansion effect when dragging
                      }}
                    />
                  )}
                />
                <div className="mt-2 flex justify-between text-sm">
                  <span>£{(priceRange[0] / 100).toFixed(2)}</span> {/* Divide by 100 before formatting */}
                  <span>£{(priceRange[1] / 100).toFixed(2)}</span>
                </div>
              </div>
            </AccordionBtn>
          )}
        </>
      )}
    </div>
  );
};

export default FacetFilters;
